/**
 * Solicitudes de aprobación (descuentos, precios, salidas, anulaciones y devoluciones)
 *
 * El vendedor pide, el administrador aprueba, y lo aprobado se aplica en ese momento.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'

export type ApprovalKind = 'discount' | 'price_change' | 'stock_out' | 'annulment' | 'devolution'
export type ApprovalStatus = 'pending' | 'processing' | 'applied' | 'rejected' | 'cancelled'

export type PriceFields = Partial<{
  purchase_cost: number
  profit_1: number
  profit_2: number
  profit_3: number
  sale_price_1_iva: number
  sale_price_2_iva: number
  sale_price_3_iva: number
  applied_sale_tax: boolean
}>

export interface DevolutionLine {
  original_line_id: number
  quantity: number
  unit_price: number
  article_code: string | null
  description: string | null
  tax_rate: number
}

export interface ApprovalRequest {
  id: number
  kind: ApprovalKind
  status: ApprovalStatus
  created_at: string
  requested_by_id: number | null
  requested_by_name: string | null
  request_note: string | null
  bill_id: number | null
  bill_number: string | null
  bill_client_name: string | null
  bill_total: number | null
  line_id: number | null
  discount_percent: number | null
  article_id: number | null
  article_code: string | null
  article_description: string | null
  article_stock: number | null
  quantity: number | null
  line_quantity: number | null
  line_unit_price: number | null
  requested_prices: PriceFields | null
  previous_prices: PriceFields | null
  credit_lines: DevolutionLine[] | null
  credit_total: number | null
  resolved_by_name: string | null
  resolved_at: string | null
  resolution_note: string | null
  cancelled_by_name: string | null
  cancelled_at: string | null
  cancel_note: string | null
  applied_at: string | null
  unseen: boolean
}

export const OPEN_APPROVAL_STATUSES: ApprovalStatus[] = ['pending', 'processing']

const KEY = 'approval-requests'

interface ListFilters {
  status?: ApprovalStatus[]
  kind?: ApprovalKind
  billId?: number
  articleId?: number
}

export function useApprovalRequests(filters: ListFilters = {}, options: { enabled?: boolean; poll?: boolean } = {}) {
  return useQuery({
    queryKey: [KEY, 'list', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = {}
      if (filters.status?.length) params.status = filters.status.join(',')
      if (filters.kind) params.kind = filters.kind
      if (filters.billId) params.bill_id = filters.billId
      if (filters.articleId) params.article_id = filters.articleId
      const response = await api.get<ApprovalRequest[]>('/approval-requests', { params })
      return response.data
    },
    enabled: options.enabled ?? true,
    staleTime: 0,
    refetchInterval: options.poll ? 15_000 : false,
  })
}

// La campana pregunta cada 30 s: en Vercel no conviene una conexión abierta.
// Al admin le cuenta lo pendiente; al vendedor, las respuestas que no vio.
export function useApprovalPendingCount(enabled = true) {
  return useQuery({
    queryKey: [KEY, 'pending-count'],
    queryFn: async () => {
      const response = await api.get<{ count: number }>('/approval-requests/pending-count')
      return response.data.count
    },
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  })
}

export function useMarkApprovalsSeen() {
  const queryClient = useQueryClient()
  return useMutation({
    // Sin ids marca todas; con ids, solo esas (la respuesta que se vio en la línea).
    mutationFn: async (vars: { ids?: number[] } = {}) =>
      (await api.post('/approval-requests/mark-seen', vars)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY, 'pending-count'] }),
  })
}

function useApprovalMutation<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    // Se devuelve la promesa: quien llama ve la lista ya actualizada.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useRequestDiscount() {
  return useApprovalMutation(
    async (vars: { bill_id: number; line_id: number; discount_percent: number; note?: string }) =>
      (await api.post<ApprovalRequest>('/approval-requests/discount', vars)).data
  )
}

export function useRequestPriceChange() {
  return useApprovalMutation(
    async (vars: PriceFields & { article_id: number; note?: string }) =>
      (await api.post<ApprovalRequest>('/approval-requests/price-change', vars)).data
  )
}

export function useRequestStockOut() {
  return useApprovalMutation(
    async (vars: { article_id: number; quantity: number; note: string }) =>
      (await api.post<ApprovalRequest>('/approval-requests/stock-out', vars)).data
  )
}

export function useRequestAnnulment() {
  return useApprovalMutation(
    async (vars: { bill_id: number; note?: string }) =>
      (await api.post<ApprovalRequest>('/approval-requests/annulment', vars)).data
  )
}

export function useRequestDevolution() {
  return useApprovalMutation(
    async (vars: {
      bill_id: number
      note?: string
      lines: { original_line_id: number; quantity: number; unit_price: number }[]
    }) => (await api.post<ApprovalRequest>('/approval-requests/devolution', vars)).data
  )
}

export function useApproveRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: number; note?: string }) =>
      (await api.post<ApprovalRequest>(`/approval-requests/${vars.id}/approve`, { note: vars.note })).data,
    onSuccess: async (data) => {
      if (data.kind === 'discount') {
        queryClient.invalidateQueries({ queryKey: ['bill', String(data.bill_id)] })
      }
      if (data.kind === 'price_change' || data.kind === 'stock_out') {
        queryClient.invalidateQueries({ queryKey: ['articles'] })
      }
      if (data.kind === 'annulment' || data.kind === 'devolution') {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
        queryClient.invalidateQueries({ queryKey: ['bill'] })
        queryClient.invalidateQueries({ queryKey: ['articles'] })
      }
      await queryClient.invalidateQueries({ queryKey: [KEY] })
    },
  })
}

export function useRejectRequest() {
  return useApprovalMutation(
    async (vars: { id: number; note: string }) =>
      (await api.post<ApprovalRequest>(`/approval-requests/${vars.id}/reject`, { note: vars.note })).data
  )
}

export function useCancelRequest() {
  return useApprovalMutation(
    async (vars: { id: number; note: string }) =>
      (await api.post<ApprovalRequest>(`/approval-requests/${vars.id}/cancel`, { note: vars.note })).data
  )
}
